import { API_BASE } from '../../../config/api';
import { AUTH_TOKEN_KEY } from '../../../utils/authSession';
import { optimizeImageFile } from '../../../utils/imageOptimization';

const query = (params = {}) => {
  const values = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined && value !== 'ALL') values.set(key, value);
  });
  const suffix = values.toString();
  return suffix ? `?${suffix}` : '';
};

export const groundsRequest = async (path, options = {}) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY) || '';
  const headers = new Headers(options.headers || {});
  if (!(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(`${API_BASE}/api/superadmin${path}`, { ...options, headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) { const error = new Error(payload.error || payload.message || 'Unable to complete the Grounds operation.'); error.status = response.status; error.code = payload.code; throw error; }
  return payload;
};

export const groundsAdminApi = {
  list: (params) => groundsRequest(`/grounds${query(params)}`),
  detail: (groundId) => groundsRequest(`/grounds/${groundId}`),
  create: (body) => groundsRequest('/grounds', { method: 'POST', body: JSON.stringify(body) }),
  update: (groundId, body) => groundsRequest(`/grounds/${groundId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  review: (groundId, body) => groundsRequest(`/grounds/${groundId}/review`, { method: 'POST', body: JSON.stringify(body) }),
  remove: (groundId) => groundsRequest(`/grounds/${groundId}`, { method: 'DELETE' }),
  searchUsers: (search) => groundsRequest(`/grounds/users/search${query({ search })}`),
  assignManager: (groundId, body) => groundsRequest(`/grounds/${groundId}/managers`, { method: 'POST', body: JSON.stringify(body) }),
  reviewManager: (groundId, assignmentId, body) => groundsRequest(`/grounds/${groundId}/managers/${assignmentId}/review`, { method: 'POST', body: JSON.stringify(body) }),
  removeManager: (groundId, assignmentId) => groundsRequest(`/grounds/${groundId}/managers/${assignmentId}`, { method: 'DELETE' }),
  exceptions: (groundId, params) => groundsRequest(`/grounds/${groundId}/exceptions${query(params)}`),
  createException: (groundId, body) => groundsRequest(`/grounds/${groundId}/exceptions`, { method: 'POST', body: JSON.stringify(body) }),
  updateException: (groundId, exceptionId, body) => groundsRequest(`/grounds/${groundId}/exceptions/${exceptionId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  cancelException: (groundId, exceptionId, reason) => groundsRequest(`/grounds/${groundId}/exceptions/${exceptionId}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  day: (groundId, date) => groundsRequest(`/grounds/${groundId}/day${query({ date })}`),
  audit: (groundId, params) => groundsRequest(`/grounds/${groundId}/audit${query(params)}`),
  requests: (params) => groundsRequest(`/grounds/requests${query(params)}`),
  requestDetail: (requestId) => groundsRequest(`/grounds/requests/${requestId}`),
  respondRequest: (requestId, body) => groundsRequest(`/grounds/requests/${requestId}/respond`, { method: 'POST', body: JSON.stringify(body) }),
  bookings: (params) => groundsRequest(`/grounds/bookings${query(params)}`),
  bookingDetail: (bookingId) => groundsRequest(`/grounds/bookings/${bookingId}`),
  cancelBooking: (bookingId, reason) => groundsRequest(`/grounds/bookings/${bookingId}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  upload: async (file) => { const body = new FormData(); body.append('image', await optimizeImageFile(file, 'PHOTO')); return groundsRequest('/grounds/media', { method: 'POST', body }); },
};

export const formatGroundDate = (value, options = {}) => value ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: options.time === false ? undefined : 'short', ...options }).format(new Date(value)) : '—';
export const formatLocalDateTimeInput = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
export const formatMoney = (minor = 0, currency = 'INR') => new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(minor || 0) / 100);
export const titleCase = (value) => String(value || '').toLowerCase().replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
